'use client'

import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState, useEffect, useRef } from 'react'
import { MobileMenuContent } from './MobileMenuContent'
import { MobileSocialIcons } from './MobileMenuContent/MobileSocialIcons'

export const MobileMenu = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Focus management
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement
      // Focus will be managed by Headless UI Dialog
    } else if (previousActiveElement.current) {
      // Return focus to the button that opened the menu
      previousActiveElement.current.focus()
    }
  }, [isMobileMenuOpen])

  const handleClose = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <>
      <div className="flex items-center lg:hidden">
        <button
          ref={menuButtonRef}
          type="button"
          className="-ml-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-2 text-vi-gray hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-vi-red focus:ring-offset-2 transition-colors duration-200"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={isMobileMenuOpen}
        >
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      <Dialog
        open={isMobileMenuOpen}
        onClose={handleClose}
        className="relative z-100 lg:hidden"
        initialFocus={closeButtonRef}
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300 ease-in-out data-closed:opacity-0"
        />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen max-w-md transform transition duration-300 ease-in-out data-closed:-translate-x-full"
              >
                <div className="flex h-full flex-col bg-white pt-[env(safe-area-inset-top)] shadow-2xl">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-vi-gray">Vanishing Inc. Menu</h2>
                    <button
                      ref={closeButtonRef}
                      type="button"
                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-vi-gray hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-vi-red focus:ring-offset-2 transition-colors duration-200"
                      onClick={handleClose}
                      aria-label="Close menu"
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-y-auto px-4 py-4">
                    <MobileMenuContent setIsMobileMenuOpen={handleClose} />
                  </div>

                  {/* Social Icons at bottom */}
                  <MobileSocialIcons />
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
}
